import { Directive, ElementRef, EventEmitter, HostListener, Input, Optional, Output, Self } from '@angular/core';
import { NgModel } from '@angular/forms';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { ScrollableDirective } from 'directives/scrollable.directive';
import { SelectListItemModel } from 'models/SelectList';
import { DropdownListService } from './dropdown-list';

/**
 * Binds an input element to a dropdown list, allowing the user to filter the list by typing in the input, and navigate using the keyboard.
 **/
@Directive({ selector: '[ngModel][dropdown]' })
export class DropdownInputDirective {
	@Input() charactersForSuggestions = 0;
	@Input() pristine = true;
	@Input() dropdownParent?: ElementRef<HTMLElement>;
	@Output() selectedItemChange = new EventEmitter<SelectListItemModel>();
	constructor(
		private readonly element: ElementRef<HTMLElement>,
		private readonly dropdown: DropdownListService,
		private readonly customSelect: CustomSelectComponent,
		@Self() private readonly ngModel: NgModel,
		@Optional() private readonly scrollParent?: ScrollableDirective) {

		// Unsubscribe not necessary because ngModel is destroyed when directive is destroyed
		ngModel.valueChanges?.subscribe(() => {
			if (this.pristine) ngModel.control.markAsPristine();
			this.updateFilter();
		});

	}
	
	private showDropdownList() {
		this.dropdown
			.show(this.customSelect.selectList, this.customSelect.autoSort, this.dropdownParent ?? this.element, this.scrollParent)
			.subscribe(item => this.selectedItemChange.next(item));
	}
	private updateFilter() {
		if (this.ngModel.value?.length >= this.charactersForSuggestions) {
			this.dropdown.filter = this.ngModel.value;
		}
		else this.dropdown.filter = undefined;
	}

	@HostListener('click')
	@HostListener('focusin') focusIn() {
		this.dropdown.removeHighlight();
		this.dropdown.filter = this.charactersForSuggestions === 0 ? '' : undefined; // If no input threshold for filtered list, show full ist when activating input
		this.showDropdownList();
	}
	@HostListener('focusout') focusOut() {
		this.dropdown.hide();
	}
	@HostListener('keyup', ['$event']) keyup(event: KeyboardEvent) {
		this.showDropdownList();
		const ignoredKeys = ['Enter', 'ArrowDown', 'ArrowUp', 'Tab', 'Escape'];
		if (ignoredKeys.indexOf(event.key) === -1) this.updateFilter();
	}
	@HostListener('paste') paste() { this.updateFilter(); }

	@HostListener('keydown', ['$event']) keyInput(event: KeyboardEvent) {

		switch (event.key) {
			case 'Enter':
				this.dropdown.selectHighlightedItem();
				return false;
			case 'ArrowDown':
				this.dropdown.moveHighlight(1);
				return false;
			case 'ArrowUp':
				this.dropdown.moveHighlight(-1);
				return false;
			case 'Tab':
				this.focusOut();
				break;
			case 'Escape':
				this.focusOut();
				event.stopPropagation();
				return false;
		}
		return true;
	}
}

